import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCampaign } from '../../contexts/CampaignContext';
import CampaignContactList from './CampaignContactList';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download, BarChart, PieChart } from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const CampaignStatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'draft':
                return 'bg-gray-200 text-gray-800';
            case 'scheduled':
                return 'bg-blue-200 text-blue-800';
            case 'in_progress':
                return 'bg-yellow-200 text-yellow-800';
            case 'paused':
                return 'bg-orange-200 text-orange-800';
            case 'completed':
                return 'bg-green-200 text-green-800';
            case 'cancelled':
                return 'bg-red-200 text-red-800';
            case 'failed':
                return 'bg-red-200 text-red-800';
            default:
                return 'bg-gray-200 text-gray-800';
        }
    };

    return (
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(status)}`}>
            {status.replace('_', ' ')}
        </span>
    );
};

const CampaignDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        currentCampaign,
        fetchCampaign,
        deleteCampaign,
        startCampaign,
        scheduleCampaign,
        cancelSchedule,
        pauseCampaign,
        resumeCampaign,
        cancelCampaign,
        getCampaignStatus,
        fetchCampaignReport,
        loading,
        error
    } = useCampaign();

    const [status, setStatus] = useState(null);
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [campaignReport, setCampaignReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState(null);

    // Refs for chart canvas elements
    const pieChartRef = useRef(null);
    const barChartRef = useRef(null);

    // Load campaign data
    useEffect(() => {
        loadCampaign();
    }, [id]);

    // Periodically refresh status for active campaigns
    useEffect(() => {
        let statusInterval;

        if (
            currentCampaign &&
            ['in_progress', 'paused', 'completed'].includes(currentCampaign.status)
        ) {
            statusInterval = setInterval(async () => {
                await refreshStatus();
            }, 10000); // Refresh every 10 seconds
        }

        return () => {
            if (statusInterval) clearInterval(statusInterval);
        };
    }, [currentCampaign]);

    // Fetch campaign report when report tab is active
    useEffect(() => {
        if (activeTab === 'report' && currentCampaign && ['completed', 'cancelled', 'failed'].includes(currentCampaign.status)) {
            loadCampaignReport();
        }
    }, [activeTab, currentCampaign]);

    const loadCampaign = async () => {
        await fetchCampaign(id);
        await refreshStatus();
    };

    const refreshStatus = async () => {
        try {
            const campaignStatus = await getCampaignStatus(id);
            if (campaignStatus) {
                setStatus(campaignStatus);

                // Update current campaign status if it has changed
                if (currentCampaign && campaignStatus.status !== currentCampaign.status) {
                    await fetchCampaign(id); // Refresh the entire campaign data
                    console.log("Campaign status changed from", currentCampaign.status, "to", campaignStatus.status);
                }
            }
        } catch (error) {
            console.error("Error refreshing campaign status:", error);
        }
    };

    // Load campaign report function
    const loadCampaignReport = async () => {
        setReportLoading(true);
        setReportError(null);
        try {
            const report = await fetchCampaignReport(id);
            console.log("Initial report structure:", report);
            setCampaignReport(report);
        } catch (err) {
            setReportError(err.message || 'Failed to load campaign report');
            console.error('Error loading campaign report:', err);
        } finally {
            setReportLoading(false);
        }
    };

    // Handle PDF export
    const handleExportPDF = () => {
        if (!campaignReport || !campaignReport.report) {
            console.error('No report data to export');
            return;
        }

        try {
            const doc = new jsPDF();
            const report = campaignReport.report;

            // Add title
            doc.setFontSize(18);
            doc.text('Campaign Report: ' + currentCampaign.name, 14, 20);

            // Add campaign info
            doc.setFontSize(12);
            doc.text(`Status: ${currentCampaign.status}`, 14, 30);
            doc.text(`Created: ${formatDate(currentCampaign.createdAt)}`, 14, 37);
            if (currentCampaign.completedAt) {
                doc.text(`Completed: ${formatDate(currentCampaign.completedAt)}`, 14, 44);
            }

            // Delivery Summary
            doc.setFontSize(14);
            doc.text('Delivery Summary', 14, 54);

            // Create summary table
            const summaryData = [
                ['Total', 'Delivered', 'Read', 'Failed', 'Avg. Delivery Time'],
                [
                    report.totalContacts || 0,
                    `${report.statusCounts?.delivered || 0} (${(report.statusCounts?.delivered || 0) * 100 / (report.totalContacts || 1)}%)`,
                    `${report.statusCounts?.read || 0} (${report.readRate || 0}%)`,
                    `${report.statusCounts?.failed || 0} (${report.failureRate || 0}%)`,
                    report.averageDeliveryTime ? `${(report.averageDeliveryTime / 1000).toFixed(1)}s` : 'N/A'
                ]
            ];

            // Use autoTable as a plugin
            autoTable(doc, {
                startY: 58,
                head: [summaryData[0]],
                body: [summaryData[1]],
                theme: 'grid',
                headStyles: { fillColor: [66, 139, 202] }
            });

            let finalY = doc.lastAutoTable?.finalY || 80;

            // Add note about delivery rate below the table
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Note: Success rate (delivery rate) of ${report.deliveryRate}% includes both delivered (${report.statusCounts?.delivered || 0}) and read (${report.statusCounts?.read || 0}) messages out of ${report.totalContacts} total.`, 14, finalY + 10);
            finalY += 15;

            // Helper function to add chart with preserved aspect ratio
            const addChartToPage = (chartRef, title, yPosition, maxWidth = 170) => {
                if (!chartRef.current) return yPosition;

                try {
                    doc.setFontSize(14);
                    doc.text(title, 14, yPosition + 15);

                    // Get the chart canvas and its dimensions
                    const chartCanvas = chartRef.current.canvas;
                    const chartImg = chartRef.current.toBase64Image();

                    // Calculate the dimensions to maintain aspect ratio
                    const chartAspectRatio = chartCanvas.width / chartCanvas.height;
                    const chartWidth = Math.min(maxWidth, 170); // Limit width to page width with margins
                    const chartHeight = chartWidth / chartAspectRatio;

                    // Center the chart horizontally on the page
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const xPosition = (pageWidth - chartWidth) / 2;

                    // Add the image with preserved aspect ratio
                    doc.addImage(chartImg, 'PNG', xPosition, yPosition + 20, chartWidth, chartHeight);

                    // Return the new Y position after adding the chart
                    return yPosition + 30 + chartHeight;
                } catch (chartErr) {
                    console.error(`Error adding ${title} chart to PDF:`, chartErr);
                    return yPosition + 15;
                }
            };

            // Add status distribution chart if it exists
            if (pieChartRef.current) {
                finalY = addChartToPage(pieChartRef, 'Message Status Distribution', finalY);
            }

            // Check if we need a new page before adding another chart
            if (finalY > doc.internal.pageSize.height - 100) {
                doc.addPage();
                finalY = 20;
            }

            // Add batch performance chart if it exists and there's batch data
            if (barChartRef.current && report.batchBreakdown && report.batchBreakdown.length > 0) {
                finalY = addChartToPage(barChartRef, 'Batch Performance', finalY);
            }

            // Check if we need a new page before adding tables
            if (finalY > doc.internal.pageSize.height - 100) {
                doc.addPage();
                finalY = 20;
            }

            // Add batch breakdown if available
            if (report.batchBreakdown && report.batchBreakdown.length > 0) {
                doc.setFontSize(14);
                doc.text('Batch Breakdown', 14, finalY + 15);

                const batchData = report.batchBreakdown.map(batch => [
                    batch.batchNumber,
                    batch.totalContacts,
                    formatDate(batch.startTime),
                    formatDate(batch.endTime),
                    `${batch.deliveryRate}%`
                ]);

                autoTable(doc, {
                    startY: finalY + 20,
                    head: [['Batch', 'Contacts', 'Start Time', 'End Time', 'Delivery Rate']],
                    body: batchData,
                    theme: 'grid',
                    headStyles: { fillColor: [66, 139, 202] }
                });

                finalY = doc.lastAutoTable?.finalY || finalY + 50;
            }

            // Add error breakdown if available
            if (report.errorBreakdown && report.errorBreakdown.length > 0) {
                // Check if we need a new page for the error breakdown
                if (finalY > doc.internal.pageSize.height - 100) {
                    doc.addPage();
                    finalY = 20;
                }

                doc.setFontSize(14);
                doc.text('Error Breakdown', 14, finalY + 15);

                const errorData = report.errorBreakdown.map(error => [
                    error.code,
                    error.message,
                    error.count
                ]);

                autoTable(doc, {
                    startY: finalY + 20,
                    head: [['Error Code', 'Message', 'Count']],
                    body: errorData,
                    theme: 'grid',
                    headStyles: { fillColor: [66, 139, 202] }
                });
            }

            // Add footer
            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(10);
            doc.text(`Report generated: ${formatDate(report.generatedAt)}`, 14, pageHeight - 20);
            doc.text(`Last updated: ${formatDate(report.lastUpdatedAt)}`, 14, pageHeight - 14);

            // Save the PDF
            doc.save(`campaign-report-${currentCampaign.name.replace(/\s+/g, '-')}.pdf`);

        } catch (err) {
            console.error('Failed to generate PDF:', err);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this campaign?')) {
            await deleteCampaign(id);
            navigate('/campaigns');
        }
    };

    const handleStart = async () => {
        if (window.confirm('Are you sure you want to start this campaign now?')) {
            await startCampaign(id);
            await loadCampaign();
        }
    };

    const handleSchedule = async () => {
        setShowScheduleModal(true);
    };

    const handleSubmitSchedule = async () => {
        const scheduleDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        await scheduleCampaign(id, scheduleDateTime.toISOString());
        setShowScheduleModal(false);
        await loadCampaign();
    };

    const handleCancelSchedule = async () => {
        if (window.confirm('Are you sure you want to cancel the scheduled campaign?')) {
            await cancelSchedule(id);
            await loadCampaign();
        }
    };

    const handlePause = async () => {
        await pauseCampaign(id);
        await loadCampaign();
    };

    const handleResume = async () => {
        await resumeCampaign(id);
        await loadCampaign();
    };

    const handleCancel = async () => {
        if (window.confirm('Are you sure you want to cancel this campaign?')) {
            await cancelCampaign(id);
            await loadCampaign();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    if (loading && !currentCampaign) {
        return <div className="flex justify-center p-4">Loading campaign...</div>;
    }

    if (error && !currentCampaign) {
        return <div className="text-red-500 p-4">Error: {error}</div>;
    }

    if (!currentCampaign) {
        return <div className="text-red-500 p-4">Campaign not found</div>;
    }

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">{currentCampaign.name}</h2>
                    <CampaignStatusBadge status={currentCampaign.status} />
                </div>
                <p className="text-gray-600 mt-1">{currentCampaign.description}</p>
            </div>

            {/* Campaign Actions */}
            <div className="p-4 bg-gray-50 border-b">
                <div className="flex flex-wrap gap-2">
                    {currentCampaign.status === 'draft' && (
                        <>
                            <button
                                onClick={handleStart}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                Start Now
                            </button>
                            <button
                                onClick={handleSchedule}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                Schedule
                            </button>
                            <Link
                                to={`/campaigns/${id}/edit`}
                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Edit
                            </Link>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                                Delete
                            </button>
                        </>
                    )}

                    {currentCampaign.status === 'scheduled' && (
                        <>
                            <button
                                onClick={handleStart}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                Start Now
                            </button>
                            <button
                                onClick={handleCancelSchedule}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                                Cancel Schedule
                            </button>
                        </>
                    )}

                    {currentCampaign.status === 'in_progress' && (
                        <>
                            <button
                                onClick={handlePause}
                                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            >
                                Pause
                            </button>
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                                Cancel
                            </button>
                        </>
                    )}

                    {currentCampaign.status === 'paused' && (
                        <>
                            <button
                                onClick={handleResume}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                Resume
                            </button>
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                                Cancel
                            </button>
                        </>
                    )}

                    {['completed', 'cancelled', 'failed'].includes(currentCampaign.status) && (
                        <button
                            onClick={handleDelete}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                            Delete
                        </button>
                    )}

                    <Link
                        to="/campaigns"
                        className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        Back to List
                    </Link>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                    <button
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'details'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('details')}
                    >
                        Details
                    </button>
                    <button
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'contacts'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('contacts')}
                    >
                        Contacts
                    </button>
                    <button
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'report'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('report')}
                    >
                        Report
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div className="p-4">
                {activeTab === 'details' && (
                    <div className="space-y-6">
                        {/* Campaign Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-md font-medium mb-3">Campaign Information</h3>
                                <div className="bg-gray-50 p-4 rounded-md">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">Status</p>
                                            <p className="font-medium">{currentCampaign.status.replace('_', ' ')}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Created</p>
                                            <p className="font-medium">{formatDate(currentCampaign.createdAt)}</p>
                                        </div>

                                        {currentCampaign.scheduleTime && (
                                            <div>
                                                <p className="text-sm text-gray-500">Scheduled For</p>
                                                <p className="font-medium">{formatDate(currentCampaign.scheduleTime)}</p>
                                            </div>
                                        )}

                                        {currentCampaign.startedAt && (
                                            <div>
                                                <p className="text-sm text-gray-500">Started</p>
                                                <p className="font-medium">{formatDate(currentCampaign.startedAt)}</p>
                                            </div>
                                        )}

                                        {currentCampaign.completedAt && (
                                            <div>
                                                <p className="text-sm text-gray-500">Completed</p>
                                                <p className="font-medium">{formatDate(currentCampaign.completedAt)}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-md font-medium mb-3">Campaign Settings</h3>
                                <div className="bg-gray-50 p-4 rounded-md">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">Batch Size</p>
                                            <p className="font-medium">{currentCampaign.settings?.batchSize || 50}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Message Delay</p>
                                            <p className="font-medium">{currentCampaign.settings?.delayBetweenMessages || 1000} ms</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Batch Delay</p>
                                            <p className="font-medium">{currentCampaign.settings?.delayBetweenBatches || 60000} ms</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Template Info */}
                        <div>
                            <h3 className="text-md font-medium mb-3">Template Information</h3>
                            <div className="bg-gray-50 p-4 rounded-md">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500">Template Name</p>
                                        <p className="font-medium">{currentCampaign.templateName}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Template ID</p>
                                        <p className="font-medium">{currentCampaign.templateId}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Language</p>
                                        <p className="font-medium">{currentCampaign.templateLanguage}</p>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="text-sm text-gray-500 mb-2">Template Components</p>
                                    {currentCampaign.templateComponents?.map((component, idx) => (
                                        <div key={idx} className="mb-3 p-3 border border-gray-200 rounded-md">
                                            <p className="text-xs font-semibold uppercase text-gray-500 mb-1">
                                                {component.type}
                                            </p>
                                            {component.parameters?.map((param, pIdx) => (
                                                <div key={pIdx} className="text-sm">
                                                    {param.type === 'text' && (
                                                        <span>{param.text}</span>
                                                    )}
                                                    {param.type === 'image' && (
                                                        <div className="flex items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            <span className="font-medium">[IMAGE]</span>
                                                            {param.image?.link && (
                                                                <a
                                                                    href={param.image.link}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="ml-2 text-xs text-blue-500 hover:underline"
                                                                >
                                                                    View
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                    {param.type === 'video' && (
                                                        <div className="flex items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <span className="font-medium">[VIDEO]</span>
                                                            {param.video?.link && (
                                                                <a
                                                                    href={param.video.link}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="ml-2 text-xs text-blue-500 hover:underline"
                                                                >
                                                                    View
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                    {param.type === 'document' && (
                                                        <div className="flex items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            <span className="font-medium">[DOCUMENT]</span>
                                                            {param.document?.link && (
                                                                <a
                                                                    href={param.document.link}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="ml-2 text-xs text-blue-500 hover:underline"
                                                                >
                                                                    View
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Campaign Progress */}
                        {status && (
                            <div>
                                <h3 className="text-md font-medium mb-3">Campaign Progress</h3>
                                <div className="bg-gray-50 p-4 rounded-md">
                                    {status.progress && (
                                        <div className="mb-4">
                                            <div className="flex justify-between mb-1">
                                                <span className="text-sm font-medium">Progress</span>
                                                <span className="text-sm font-medium">{status.progress.progressPercentage}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className="bg-blue-600 h-2.5 rounded-full"
                                                    style={{ width: `${status.progress.progressPercentage}%` }}
                                                ></div>
                                            </div>
                                            <div className="flex justify-between text-sm text-gray-500 mt-1">
                                                <span>Batch {status.progress.currentBatch} of {status.progress.totalBatches}</span>
                                                <span>{status.progress.sentCount} sent, {status.progress.failedCount} failed</span>
                                            </div>
                                        </div>
                                    )}

                                    {status.statusCounts && (
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Pending</p>
                                                <p className="text-xl font-semibold">{status.statusCounts.pending || 0}</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Sent</p>
                                                <p className="text-xl font-semibold">{status.statusCounts.sent || 0}</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Delivered</p>
                                                <p className="text-xl font-semibold">{status.statusCounts.delivered || 0}</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Read</p>
                                                <p className="text-xl font-semibold">{status.statusCounts.read || 0}</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Failed</p>
                                                <p className="text-xl font-semibold">{status.statusCounts.failed || 0}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'contacts' && (
                    <CampaignContactList campaignId={id} campaignStatus={currentCampaign.status} />
                )}

                {activeTab === 'report' && (
                    <div>
                        {reportLoading ? (
                            <div className="p-4 text-center">
                                <div className="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-blue-600 rounded-full" role="status" aria-label="loading">
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <p className="mt-2 text-gray-500">Loading campaign report...</p>
                            </div>
                        ) : reportError ? (
                            <div className="p-4 text-center">
                                <div className="text-red-500 mb-2">{reportError}</div>
                                <button
                                    onClick={loadCampaignReport}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : !campaignReport ? (
                            <div className="p-4 text-center">
                                {['completed', 'cancelled', 'failed'].includes(currentCampaign.status) ? (
                                    <div>
                                        <p className="text-gray-500 mb-4">Report not available. The report may not have been generated yet.</p>
                                    </div>
                                ) : (
                                    <p className="text-gray-500">Campaign report will be available after the campaign is completed.</p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-medium">Campaign Report</h3>
                                    <button
                                        onClick={handleExportPDF}
                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm flex items-center"
                                    >
                                        <Download className="h-4 w-4 mr-1" />
                                        Export PDF
                                    </button>
                                </div>

                                {/* Delivery Summary */}
                                <div>
                                    <h4 className="text-md font-medium mb-3">Delivery Summary</h4>
                                    <div className="bg-gray-50 p-4 rounded-md">
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Total</p>
                                                <p className="text-xl font-semibold">{campaignReport.report?.totalContacts || 0}</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Delivered</p>
                                                <p className="text-xl font-semibold">{campaignReport.report?.statusCounts.delivered || 0}</p>
                                                <p className="text-xs text-gray-500">
                                                    <span title="Includes both delivered and read messages">Success rate: {campaignReport.report?.deliveryRate || 0}%</span>
                                                </p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Read</p>
                                                <p className="text-xl font-semibold">{campaignReport.report?.statusCounts.read || 0}</p>
                                                <p className="text-xs text-gray-500">Of total: {campaignReport.report?.readRate || 0}%</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Failed</p>
                                                <p className="text-xl font-semibold">{campaignReport.report?.statusCounts.failed || 0}</p>
                                                <p className="text-xs text-gray-500">Of total: {campaignReport.report?.failureRate || 0}%</p>
                                            </div>
                                            <div className="bg-white p-3 rounded-md shadow-sm text-center">
                                                <p className="text-sm text-gray-500">Avg. Delivery</p>
                                                <p className="text-xl font-semibold">{campaignReport.report?.averageDeliveryTime ? `${(campaignReport.report.averageDeliveryTime / 1000).toFixed(1)}s` : 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div className="mt-3 text-xs text-gray-500 p-2 bg-blue-50 rounded border border-blue-100">
                                            <p><strong>Note:</strong> Delivery rate ({campaignReport.report?.deliveryRate || 0}%) represents all successfully delivered messages, including both "Delivered" ({campaignReport.report?.statusCounts.delivered || 0}) and "Read" ({campaignReport.report?.statusCounts.read || 0}) statuses combined, as a percentage of total messages ({campaignReport.report?.totalContacts || 0}).</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Data Visualization - Charts */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Status Distribution Chart */}
                                    <div>
                                        <h4 className="text-md font-medium mb-3">Message Status Distribution</h4>
                                        <div className="bg-gray-50 p-4 rounded-md">
                                            <div className="w-full h-64 flex items-center justify-center">
                                                {campaignReport.report && (
                                                    <Pie
                                                        ref={pieChartRef}
                                                        data={{
                                                            labels: [
                                                                ...(campaignReport.report.statusCounts.pending ? ['Pending'] : []),
                                                                ...(campaignReport.report.statusCounts.sent ? ['Sent'] : []),
                                                                ...(campaignReport.report.statusCounts.delivered ? ['Delivered'] : []),
                                                                ...(campaignReport.report.statusCounts.read ? ['Read'] : []),
                                                                ...(campaignReport.report.statusCounts.failed ? ['Failed'] : [])
                                                            ],
                                                            datasets: [
                                                                {
                                                                    data: [
                                                                        ...(campaignReport.report.statusCounts.pending ? [campaignReport.report.statusCounts.pending] : []),
                                                                        ...(campaignReport.report.statusCounts.sent ? [campaignReport.report.statusCounts.sent] : []),
                                                                        ...(campaignReport.report.statusCounts.delivered ? [campaignReport.report.statusCounts.delivered] : []),
                                                                        ...(campaignReport.report.statusCounts.read ? [campaignReport.report.statusCounts.read] : []),
                                                                        ...(campaignReport.report.statusCounts.failed ? [campaignReport.report.statusCounts.failed] : [])
                                                                    ],
                                                                    backgroundColor: [
                                                                        ...(campaignReport.report.statusCounts.pending ? ['#CBD5E0'] : []), // gray for pending
                                                                        ...(campaignReport.report.statusCounts.sent ? ['#90CDF4'] : []), // blue for sent
                                                                        ...(campaignReport.report.statusCounts.delivered ? ['#4FD1C5'] : []), // teal for delivered
                                                                        ...(campaignReport.report.statusCounts.read ? ['#68D391'] : []), // green for read
                                                                        ...(campaignReport.report.statusCounts.failed ? ['#FC8181'] : [])  // red for failed
                                                                    ],
                                                                    borderWidth: 1
                                                                }
                                                            ]
                                                        }}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                legend: {
                                                                    position: 'bottom',
                                                                    labels: {
                                                                        padding: 20
                                                                    }
                                                                },
                                                                tooltip: {
                                                                    callbacks: {
                                                                        label: function (context) {
                                                                            const label = context.label || '';
                                                                            const value = context.raw || 0;
                                                                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                                                                            return `${label}: ${value} (${percentage}%)`;
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Batch Performance Chart */}
                                    {campaignReport.report?.batchBreakdown && campaignReport.report.batchBreakdown.length > 0 && (
                                        <div>
                                            <h4 className="text-md font-medium mb-3">Batch Performance</h4>
                                            <div className="bg-gray-50 p-4 rounded-md">
                                                <div className="w-full h-64 flex items-center justify-center">
                                                    <Bar
                                                        ref={barChartRef}
                                                        data={{
                                                            labels: campaignReport.report.batchBreakdown.map(batch => `Batch ${batch.batchNumber}`),
                                                            datasets: [
                                                                {
                                                                    label: 'Delivery Rate (%)',
                                                                    data: campaignReport.report.batchBreakdown.map(batch => batch.deliveryRate),
                                                                    backgroundColor: '#4299E1'
                                                                }
                                                            ]
                                                        }}
                                                        options={{
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            plugins: {
                                                                legend: {
                                                                    position: 'bottom'
                                                                },
                                                                title: {
                                                                    display: false
                                                                }
                                                            },
                                                            scales: {
                                                                y: {
                                                                    beginAtZero: true,
                                                                    max: 100,
                                                                    title: {
                                                                        display: true,
                                                                        text: 'Delivery Rate (%)'
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Error Breakdown */}
                                {campaignReport.report?.errorBreakdown && campaignReport.report.errorBreakdown.length > 0 && (
                                    <div>
                                        <h4 className="text-md font-medium mb-3">Error Breakdown</h4>
                                        <div className="bg-gray-50 p-4 rounded-md">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error Code</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {campaignReport.report.errorBreakdown.map((error, idx) => (
                                                            <tr key={idx}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{error.code}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{error.message}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{error.count}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Batch Breakdown */}
                                {campaignReport.report?.batchBreakdown && campaignReport.report.batchBreakdown.length > 0 && (
                                    <div>
                                        <h4 className="text-md font-medium mb-3">Batch Breakdown</h4>
                                        <div className="bg-gray-50 p-4 rounded-md">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacts</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery Rate</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {campaignReport.report.batchBreakdown.map((batch) => (
                                                            <tr key={batch.batchNumber}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{batch.batchNumber}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{batch.totalContacts}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(batch.startTime)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(batch.endTime)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{batch.deliveryRate}%</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="text-center text-sm text-gray-500">
                                    <p>Report generated: {formatDate(campaignReport.report?.generatedAt)}</p>
                                    <p>Last updated: {formatDate(campaignReport.report?.lastUpdatedAt)}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Schedule Modal */}
            {showScheduleModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-medium mb-4">Schedule Campaign</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    min={new Date().toISOString().split('T')[0]}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                                <input
                                    type="time"
                                    value={scheduleTime}
                                    onChange={(e) => setScheduleTime(e.target.value)}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowScheduleModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitSchedule}
                                disabled={!scheduleDate || !scheduleTime}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300"
                            >
                                Schedule
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignDetails;